import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Upload, FileText, User, Trash2, Info, Clock } from "lucide-react";
import { Button } from "./Button";
import { Card } from "./card";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { ContractCreateDto, ContractHourlyTypeEnum, ContractStatusEnum, ContractTypeEnum, MilestoneCreateDto, MilestoneStatusEnum } from "../../modules/shared/model";
import { createContract, updateContract, fetchContract, uploadContractFile, clearCurrentContract } from "../../redux/Slices/contractSlice";
import { createMilestone, updateMilestone, deleteMilestone } from "../../redux/Slices/milestoneSlice";
import { getAllCurrencies } from "../../redux/Actions/AuthAction";
import { fetchCategoryFormSettings, fetchLatestCategoryFormSettings } from "../../redux/Actions/CategoryFormSettingsAction";
import { fetchDeposits } from "../../redux/Slices/depositSlice";
import { Offer } from "./RequestQuoteModal";
import { GlobalService } from "../../modules";
import MilestoneDialog from "./MilestoneDialog";
import InformationDialog from "./InformationDialog";
import InformationalVideosDialog from "./InformationalVideosDialog";
import { PlatformFeeUtils, PLATFORM_CONFIG } from "../../config/platformConfig";
import { EscrowStatus } from "../../services/escrowAPI";
import { getWebWorkTrackerFee } from "../../utils/categoryFormSettingsUtils";
import { findApplicableDepositRule, calculateDepositAmount } from "../../utils/depositRuleUtils";
import { DepositRuleTypeEnum } from "../../modules/shared/model";
import { getDynamicCurrencySymbol } from "../../lib/currencyUtils";
import { useTranslation } from "react-i18next";

interface Props {
	isOpen: boolean;
	onClose: () => void;
	existingContractId?: string;
	defaultIsMilestone?: boolean;
	offer?: Offer;
	clientOffers?: any[];
	onOfferSelect?: (offer: any) => void;
}

const ContractMilestoneModal: React.FC<Props> = ({ isOpen, onClose, existingContractId, defaultIsMilestone = false, offer, clientOffers = [], onOfferSelect }) => {
	const { t } = useTranslation();
	const dispatch = useAppDispatch();

	// Reset all state to initial values
	const resetAllState = () => {
		// Reset file states
		setUploadedFiles([]);
		setRemovedFiles([]);
		setIsUploading(false);
		setUploadProgress(0);
		setUploadingFileName("");
		
		// Reset form states
		setAcceptTerms(false);
		setMilestonesTotalError(null);
		setMilestoneRowErrors({});
		
		// Reset dialog states
		setShowFeaturesDialog(false);
		setShowMilestonesDialog(false);
		setShowInformationDialog(false);
		setShowVideosDialog(false);
		setShowAmountWarningDialog(false);
		
		// Reset amount and contract states
		setCurrentTotalAmount(0);
		setContractId(null);
		setOriginalMilestones([]);
		setIsEditMode(false);
		setMilestonesCreated(false);
		setContractCreated(false);
		setHasFundedEscrowPayment(false);
		setEmergencyCost(0);
		setSelectedOfferFromDropdown(null);
		
		// Reset contract form to default values
		setContractForm({
			files: [],
			offer: { id: "" },
			enableTracker: false,
			canAutomaticWithdraw: false,
			weeklyLimit: null,
			hourlyType: { id: ContractHourlyTypeEnum.Occasional },
			isMilestone: true,
			isRenewable: false,
			type: { id: ContractTypeEnum.Fixed }, // Default to Fixed
			status: { id: ContractStatusEnum.Active },
			amount: null,
			description: "",
			title: "",
		});
		
		// Reset milestone form to default values
		setMilestoneForm({
			status: { id: MilestoneStatusEnum.Pending },
			dueDate: new Date().toISOString(),
			amount: null,
			description: "",
			contract: { id: "" },
		});
		
		// Reset milestones array to default
		setMilestones([
			{ description: "", amount: null, dueDate: undefined },
		]);
	};

	// Enhanced close handler that clears current contract and resets all state
	const handleClose = () => {
		dispatch(clearCurrentContract());
		resetAllState();
		onClose();
	};

	// Helper function to extract file information from path
	const extractFileInfo = (file: any) => {
		const filePath = file.path || file.url || "";
		const fileName = filePath.split("/").pop() || file.name || file.originalName || "Unknown file";
		const fileExtension = fileName.split(".").pop()?.toLowerCase() || "";

		// Map file extensions to MIME types
		const getMimeType = (ext: string) => {
			const mimeTypes: { [key: string]: string } = {
				"pdf": "application/pdf",
				"doc": "application/msword",
				"docx": "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
				"jpg": "image/jpeg",
				"jpeg": "image/jpeg",
				"png": "image/png",
				"gif": "image/gif",
				"txt": "text/plain",
				"zip": "application/zip",
				"rar": "application/x-rar-compressed"
			};
			return mimeTypes[ext] || "application/octet-stream";
		};

		return {
			id: file.id,
			name: fileName,
			url: filePath,
			type: file.type || file.mimeType || getMimeType(fileExtension)
		};
	};
	const { loading: contractsLoading, current: currentContract, uploading: contractsUploading } = useAppSelector((s: any) => s.contracts);
	const { loading: milestonesLoading, items: existingMilestones } = useAppSelector((s: any) => s.milestones);
	const { currencies, user } = useAppSelector((s: any) => s.auth);
	const { settings: categoryFormSettings, setting } = useAppSelector((s: any) => s.categoryFormSettings);
	const { items: depositRules = [] } = useAppSelector((s: any) => s.deposits);
	const isSubmitting = contractsLoading || milestonesLoading || contractsUploading;

	const [uploadedFiles, setUploadedFiles] = useState<Array<{ id: string; name: string; url?: string; type?: string }>>([]);
	const [removedFiles, setRemovedFiles] = useState<Array<{ id: string; name: string; url?: string; type?: string }>>([]);
	const [isUploading, setIsUploading] = useState(false);
	const [uploadProgress, setUploadProgress] = useState(0);
	const [uploadingFileName, setUploadingFileName] = useState("");
	const [acceptTerms, setAcceptTerms] = useState(false);
	const [milestonesTotalError, setMilestonesTotalError] = useState<string | null>(null);
	const [milestoneRowErrors, setMilestoneRowErrors] = useState<Record<number, string>>({});
	const [showFeaturesDialog, setShowFeaturesDialog] = useState(false);
	const [showMilestonesDialog, setShowMilestonesDialog] = useState(false);
	const [showInformationDialog, setShowInformationDialog] = useState(false);
	const [showVideosDialog, setShowVideosDialog] = useState(false);
	const [showAmountWarningDialog, setShowAmountWarningDialog] = useState(false);
	const [currentTotalAmount, setCurrentTotalAmount] = useState(0);
	const [contractId, setContractId] = useState<string | null>(existingContractId || null);
	
	// Clear current contract if no existing contract ID
	useEffect(() => {
		if (!existingContractId) {
			dispatch(clearCurrentContract());
		}
	}, [existingContractId, dispatch]);

	// Reset state when modal is closed
	useEffect(() => {
		if (!isOpen) {
			// Reset state when modal is closed
			setTimeout(() => {
				resetAllState();
			}, 300); // Small delay to allow for smooth closing animation
		}
	}, [isOpen]);
	const [originalMilestones, setOriginalMilestones] = useState<Array<{ id?: string; description: string; amount: number; dueDate?: string }>>([]);
	const [isEditMode, setIsEditMode] = useState(false);
	const [milestonesCreated, setMilestonesCreated] = useState(false);
	const [contractCreated, setContractCreated] = useState(false);
	const [hasFundedEscrowPayment, setHasFundedEscrowPayment] = useState(false);
	const [emergencyCost, setEmergencyCost] = useState(0);
	const [selectedOfferFromDropdown, setSelectedOfferFromDropdown] = useState<any>(null);

	// Use selected offer from dropdown if no direct offer is provided
	const currentOffer = offer || selectedOfferFromDropdown;
	
	// Infer contract type from offer/job budgetType: 1 = Fixed, 2 = Hourly
	const initialBudgetTypeId = Number((currentOffer as any)?.job?.budgetType?.id ?? (currentOffer as any)?.budgetType?.id);
	const inferredContractType: ContractTypeEnum = initialBudgetTypeId === 2 ? ContractTypeEnum.Hourly : ContractTypeEnum.Fixed;

	// Check if job is emergency
	const isEmergency = (currentOffer as any)?.job?.isEmergency || (currentOffer as any)?.isEmergency || false;
	const emergencyTimeSlot = (currentOffer as any)?.job?.emergencyTimeSlot || (currentOffer as any)?.emergencyTimeSlot;
	const emergencyDays = (currentOffer as any)?.job?.emergencyDays || (currentOffer as any)?.emergencyDays;

	const [contractForm, setContractForm] = useState<ContractCreateDto>({
		files: [],
		offer: { id: "" },
		enableTracker: false,
		canAutomaticWithdraw: false,
		weeklyLimit: null,
		hourlyType: { id: ContractHourlyTypeEnum.Occasional },
		isMilestone: true,
		isRenewable: false,
		type: { id: inferredContractType },
		status: { id: ContractStatusEnum.Active },
		amount: null,
		description: "",
		title: "",
	} as ContractCreateDto);

	useEffect(() => {
		setContractForm({
			files: [],
			offer: { id: "" },
			enableTracker: false,
			canAutomaticWithdraw: false,
			weeklyLimit: null,
			hourlyType: { id: ContractHourlyTypeEnum.Occasional },
			isMilestone: true,
			isRenewable: false,
			type: { id: inferredContractType },
			status: { id: ContractStatusEnum.Active },
			amount: (currentOffer as any)?.amount ?? (currentOffer as any)?.job?.budget ?? null,
			description: (currentOffer as any)?.description || (currentOffer as any)?.job?.description || "",
			title: (currentOffer as any)?.title || (currentOffer as any)?.job?.title || "",
		});

		return () => {

		};
	}, [currentOffer]);


	const [milestoneForm, setMilestoneForm] = useState<MilestoneCreateDto>({
		status: { id: MilestoneStatusEnum.Pending },
		dueDate: new Date().toISOString(),
		amount: null,
		description: "",
		contract: { id: "" },
	});

	// Local milestone list for Fixed contracts
	const [milestones, setMilestones] = useState<Array<{ id?: string; description: string; amount: number; dueDate?: string }>>([
		{ description: "", amount: null, dueDate: undefined },
	]);

	// Features list for renewable contracts
	const renewableFeatures = [
		t("contractMilestoneModal.feature1"),
		t("contractMilestoneModal.feature2"),
		t("contractMilestoneModal.feature3"),
		t("contractMilestoneModal.feature4"),
		t("contractMilestoneModal.feature5"),
		t("contractMilestoneModal.feature6"),
		t("contractMilestoneModal.feature7"),
		t("contractMilestoneModal.feature8"),
		t("contractMilestoneModal.feature9")
	];

	const addMilestoneRow = () => {
		setMilestones((prev) => [...prev, { description: "", amount: null, dueDate: undefined }]);
	};

	const removeMilestoneRow = (index: number) => {
		setMilestones((prev) => prev.filter((_, i) => i !== index));
	};

	const updateMilestoneRow = (index: number, patch: Partial<{ id?: string; description: string; amount: number; dueDate?: string }>) => {
		setMilestones((prev) => prev.map((m, i) => (i === index ? { ...m, ...patch } : m)));
	};

	// Helper function to check if a milestone is funded by checking escrows array
	const isMilestoneFunded = (milestoneId: string) => {
		if (!currentContract?.escrows || !Array.isArray(currentContract.escrows)) {
			return false;
		}

		// Find escrow for this milestone
		const milestoneEscrow = currentContract.escrows.find((escrow: any) =>
			escrow.milestone?.id === milestoneId
		);

		if (!milestoneEscrow) {
			return false;
		}

		// Check if escrow status is funded
		return Number(milestoneEscrow.status?.id) === 2;
	};

	// Helper function to check if any milestone is funded (for contract-level restrictions)
	const hasAnyFundedMilestone = () => {
		if (!currentContract?.escrows || !Array.isArray(currentContract.escrows)) {
			return false;
		}

		return currentContract.escrows.some((escrow: any) => {
			return Number(escrow.status?.id) === 2;
		});
	};

	// Calculate emergency cost based on time slot and day type
	const calculateEmergencyCost = (emergencyTimeSlot: any, emergencyDays: any) => {
		// Get settings from category form settings
		const settings = setting;

		if (!settings) return 80; // Default fallback

		// Get time slot and day type IDs
		const timeSlotId = Number(emergencyTimeSlot?.id);
		const dayTypeId = Number(emergencyDays?.id);

		// Determine the price based on time slot and day type
		let price = 80; // Default fallback

		if (timeSlotId === 1) { // Day (6 AM - 6 PM)
			if (dayTypeId === 1) { // Weekday
				price = settings.DayWeekdayPrice || 80;
			} else if (dayTypeId === 2) { // Weekend
				price = settings.DayWeekendPrice || 80;
			} else if (dayTypeId === 3) { // Holiday
				price = settings.DayHolidayPrice || 80;
			}
		} else if (timeSlotId === 2) { // Evening (6 PM - 10 PM)
			if (dayTypeId === 1) { // Weekday
				price = settings.EveningWeekdayPrice || 80;
			} else if (dayTypeId === 2) { // Weekend
				price = settings.EveningWeekendPrice || 80;
			} else if (dayTypeId === 3) { // Holiday
				price = settings.EveningHolidayPrice || 80;
			}
		} else if (timeSlotId === 3) { // Night (10 PM - 6 AM)
			if (dayTypeId === 1) { // Weekday
				price = settings.NightWeekdayPrice || 80;
			} else if (dayTypeId === 2) { // Weekend
				price = settings.NightWeekendPrice || 80;
			} else if (dayTypeId === 3) { // Holiday
				price = settings.NightHolidayPrice || 80;
			}
		}

		return price;
	};

	// Calculate hourly milestone amount (hourly rate * weekly hours + additional fees)
	const calculateHourlyMilestoneAmount = () => {
		const hourlyRate = Number(contractForm.amount) || null;
		const weeklyHours = Number(contractForm.weeklyLimit) || null;
		const milestoneAmount = hourlyRate * weeklyHours;

		// Note: Smart Work Tracker fee ($9) is now stored separately in trackerAmount field
		// Emergency cost is stored separately in emergencyCost field

		return milestoneAmount;
	};

	// Create milestone for hourly contract
	const createHourlyMilestone = async (contractId: string) => {
		const hourlyRate = Number(contractForm.amount) || null;
		const weeklyHours = Number(contractForm.weeklyLimit) || null;
		const milestoneAmount = hourlyRate * weeklyHours;

		const dueDate = new Date();
		dueDate.setDate(dueDate.getDate() + 6); // 6 days from now

		const milestoneData: MilestoneCreateDto = {
			description: `Hourly Contract Payment - ${contractForm.title}`,
			amount: milestoneAmount,
			emergencyCost: isEmergency ? emergencyCost : 0,
			trackerAmount: contractForm.enableTracker ? getWebWorkTrackerFee(setting) : 0,
			dueDate: dueDate.toISOString(),
			contract: { id: contractId },
			status: { id: MilestoneStatusEnum.Pending }
		} as any;

		await dispatch(createMilestone(milestoneData));
	};

	// Update or create milestone for hourly contract
	const updateHourlyMilestone = async (contractId: string) => {
		const hourlyRate = Number(contractForm.amount) || null;
		const weeklyHours = Number(contractForm.weeklyLimit) || null;
		const milestoneAmount = hourlyRate * weeklyHours;

		const dueDate = new Date();
		dueDate.setDate(dueDate.getDate() + 6); // 6 days from now

		// Check if there's an existing hourly milestone for this contract
		const currentMilestones = currentContract.milestones || [];
		const hourlyMilestone = currentMilestones.find((m: any) =>
			m.description && m.description.includes("Hourly Contract Payment")
		);

		if (hourlyMilestone) {
			// Update existing milestone
			const updateData: Partial<MilestoneCreateDto> = {
				description: `Hourly Contract Payment - ${contractForm.title}`,
				amount: milestoneAmount,
				emergencyCost: isEmergency ? emergencyCost : 0,
				trackerAmount: contractForm.enableTracker ? getWebWorkTrackerFee(setting) : 0,
				dueDate: dueDate.toISOString(),
				contract: { id: contractId },
				status: { id: MilestoneStatusEnum.Pending }
			} as any;

			await dispatch(updateMilestone({ id: hourlyMilestone.id, data: updateData }));
		} else {
			// Create new milestone if none exists
			await createHourlyMilestone(contractId);
		}

		// Refresh contract to get updated milestone data
		await dispatch(fetchContract(contractId));
	};

	// Handle milestone operations (add, update, delete) for existing contracts
	const handleMilestoneOperations = async (contractId: string) => {
		const currentMilestones = milestones || [];
		const originalMilestonesData = originalMilestones || [];


		// Find milestones to delete (exist in original but not in current)
		const milestonesToDelete = originalMilestonesData.filter(original =>
			original.id &&
			!currentMilestones.some(current => current.id === original.id) &&
			!isMilestoneFunded(original.id) // Don't delete funded milestones
		);

		// Find milestones to update (exist in both but have changes)
		const milestonesToUpdate = currentMilestones.filter(current => {
			if (!current.id) return false; // Skip new milestones (they'll be created)

			// Skip funded milestones - they cannot be updated
			if (isMilestoneFunded(current.id)) return false;

			const original = originalMilestonesData.find(orig => orig.id === current.id);
			if (!original) return false;

			// Compare values properly
			const descriptionChanged = original.description !== current.description;
			const amountChanged = original.amount !== current.amount;
			const dueDateChanged = original.dueDate !== current.dueDate;

			return descriptionChanged || amountChanged || dueDateChanged;
		});

		// Find milestones to create (don't have an id)
		const milestonesToCreate = currentMilestones.filter(current => !current.id);


		// Delete milestones
		for (const milestone of milestonesToDelete) {
			if (milestone.id) {
				await dispatch(deleteMilestone(milestone.id));
			}
		}

		// Update milestones
		for (const milestone of milestonesToUpdate) {
			if (milestone.id) {
				const updateData: Partial<MilestoneCreateDto> = {
					description: milestone.description,
					amount: milestone.amount,
					emergencyCost: isEmergency && milestone.id === milestones[0]?.id ? emergencyCost : 0,
					trackerAmount: contractForm.enableTracker && milestone.id === milestones[0]?.id ? getWebWorkTrackerFee(setting) : 0,
					dueDate: milestone.dueDate ? new Date(milestone.dueDate).toISOString() : new Date().toISOString(),
					contract: { id: contractId },
					status: { id: MilestoneStatusEnum.Pending }
				} as any;
				await dispatch(updateMilestone({ id: milestone.id, data: updateData }));
			}
		}

		// Create new milestones
		for (const milestone of milestonesToCreate) {
			if (milestone.description && milestone.amount) {
				const createData: MilestoneCreateDto = {
					description: milestone.description,
					amount: milestone.amount,
					emergencyCost: isEmergency && milestone === milestones[0] ? emergencyCost : 0,
					trackerAmount: contractForm.enableTracker && milestone === milestones[0] ? getWebWorkTrackerFee(setting) : 0,
					dueDate: milestone.dueDate ? new Date(milestone.dueDate).toISOString() : new Date().toISOString(),
					contract: { id: contractId },
					status: { id: MilestoneStatusEnum.Pending }
				} as any;
				await dispatch(createMilestone(createData));
			}
		}
	};

	// Effect to fetch contract data when existingContractId is provided
	useEffect(() => {
		if (!isOpen || !existingContractId) return;

		// Fetch contract data (which now includes milestones)
		dispatch(fetchContract(existingContractId));
		// Reset edit mode when opening existing contract
		setIsEditMode(false);
	}, [isOpen, existingContractId, dispatch]);

	// Effect to populate form when contract data is loaded
	useEffect(() => {
		if (currentContract && existingContractId) {
			setHasFundedEscrowPayment(hasFundedEscrow());
			setContractForm({
				files: currentContract.files || [],
				offer: currentContract.offer || { id: "" },
				enableTracker: currentContract.enableTracker || false,
				canAutomaticWithdraw: currentContract.canAutomaticWithdraw || false,
				weeklyLimit: currentContract.weeklyLimit || null,
				hourlyType: { id: Number(currentContract.hourlyType.id) || ContractHourlyTypeEnum.Occasional },
				isMilestone: currentContract.isMilestone || false,
				isRenewable: currentContract.isRenewable || false,
				type: { id: Number(currentContract.type.id) || ContractTypeEnum.Fixed },
				status: { id: Number(currentContract.status.id) },
				amount: currentContract.amount || null,
				description: currentContract.description || "",
				title: currentContract.title || "",
			});

			// Set uploaded files from contract
			if (currentContract.files && currentContract.files.length > 0) {
				console.log("Contract files from backend:", currentContract.files);
				const processedFiles = currentContract.files.map((file: any) => {
					const fileInfo = extractFileInfo(file);
					console.log("Processed file info:", fileInfo);
					return fileInfo;
				});
				setUploadedFiles(processedFiles);
			}

			// Reset removed files when loading contract data
			setRemovedFiles([]);

		}
	}, [currentContract, existingContractId]);

	// Effect to populate milestones when contract data is loaded
	useEffect(() => {
		if (currentContract && currentContract.milestones && currentContract.milestones.length > 0 && (existingContractId || contractId)) {
			const milestoneData = currentContract.milestones.map((milestone: any) => ({
				id: milestone.id,
				description: milestone.description || "",
				amount: milestone.amount || null,
				dueDate: milestone.dueDate ? new Date(milestone.dueDate).toISOString() : undefined
			}));
			setMilestones(milestoneData);
			// Create a deep copy for original milestones to avoid reference issues
			setOriginalMilestones(JSON.parse(JSON.stringify(milestoneData)));
		}
	}, [currentContract, existingContractId, contractId]);

	// Calculate emergency cost when emergency data changes
	useEffect(() => {
		if (isEmergency && emergencyTimeSlot && emergencyDays) {
			const cost = calculateEmergencyCost(emergencyTimeSlot, emergencyDays);
			setEmergencyCost(cost);
		} else {
			setEmergencyCost(0);
		}
	}, [isEmergency, emergencyTimeSlot, emergencyDays, setting]);

	useEffect(() => {
		if (!isOpen) return;
		if (currentOffer?.id) {
			setContractForm((prev) => ({ ...prev, offer: { id: currentOffer.id } }));
		}
		// Fetch currencies if not already loaded
		if (!currencies || currencies.length === 0) {
			dispatch(getAllCurrencies({ isShowLoader: false }));
		}
		// Fetch category form settings if not already loaded
		if (!categoryFormSettings) {
			dispatch(fetchCategoryFormSettings());
		}
		// Fetch latest settings if not already loaded
		if (!setting) {
			dispatch(fetchLatestCategoryFormSettings());
		}
		// Fetch deposit rules if not already loaded
		if (!depositRules || depositRules.length === 0) {
			dispatch(fetchDeposits(undefined));
		}
		// Reset removed files when modal opens
		setRemovedFiles([]);
		// Reset original milestones when modal opens for editing
		if (existingContractId) {
			setOriginalMilestones([]);
			setMilestonesCreated(false);
			setContractCreated(false);
		}
	}, [isOpen, defaultIsMilestone, offer?.id, dispatch, currencies, existingContractId, setting]);

	if (!isOpen) return null;

	// Ensure contractForm is properly initialized
	if (!contractForm.type) {
		return null;
	}

	const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const files = e.target.files;
		if (!files || files.length === 0) return;
		setIsUploading(true);
		setUploadProgress(5);
		for (let i = 0; i < files.length; i++) {
			const file = files[i];
			setUploadingFileName(file.name);
			try {
				const { payload } = await dispatch(uploadContractFile(file) as any);
				const uploadedFile = payload.file;
				// The payload should contain the file data directly
				const fileId = uploadedFile?.id;
				const fileUrl = uploadedFile?.path;
				const fileName =  file.name;
				const fileType =  file.type;
				
				if (fileId) {
					const fileInfo = extractFileInfo({
						id: fileId,
						path: fileUrl,
						name: fileName,
						type: fileType
					});
					setUploadedFiles((prev) => [...prev, fileInfo]);
					setContractForm((prev) => ({ ...prev, files: [...(prev.files || []), { id: fileId }] }));
				} else {
					GlobalService.showToast("error", t("common.error"), t("contractMilestoneModal.uploadFailedNoId"));
				}
			} catch (error) {
				GlobalService.showToast("error", t("common.error"), t("contractMilestoneModal.uploadFailed"));
			}
			setUploadProgress(Math.round(((i + 1) / files.length) * 100));
		}
		setUploadingFileName("");
		setIsUploading(false);
		
		// Reset the file input
		e.target.value = "";
	};

	const handleFileRemove = (fileToRemove: { id: string; name: string; url?: string; type?: string }) => {
		// Add to removed files list
		setRemovedFiles((prev) => [...prev, fileToRemove]);

		// Remove from uploaded files
		setUploadedFiles((prev) => prev.filter(file => file.id !== fileToRemove.id));

		// Remove from contract form files
		setContractForm((prev) => ({
			...prev,
			files: (prev.files || []).filter(file => file.id !== fileToRemove.id)
		}));
	};

	const hasFundedEscrow = () => {
		return currentContract?.escrows?.some((escrow: any) =>
			Number(escrow.status?.id) === EscrowStatus.FUNDED
		);
	};

	const onSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setMilestonesTotalError(null);
		setMilestoneRowErrors({});

		// Check if total amount (including platform fees) is under $10
		let totalAmount = 0;
		if (Number(contractForm.type?.id) === ContractTypeEnum.Hourly) {
			// For hourly contracts, calculate total with platform fees
			totalAmount = calculateHourlyMilestoneAmount() + (contractForm.enableTracker ? getWebWorkTrackerFee(setting) : 0) + (isEmergency ? emergencyCost : 0);
		} else {
			// For fixed contracts, check if first milestone + additional costs meets minimum
			const firstMilestoneAmount = milestones && milestones.length > 0 ? Number(milestones[0].amount) || 0 : 0;
			const trackerAmount = contractForm.enableTracker ? getWebWorkTrackerFee(setting) : 0;
			const emergencyAmount = isEmergency ? emergencyCost : 0;
			
			// If we have milestones, check first milestone + additional costs
			if (milestones && milestones.length > 0) {
				totalAmount = firstMilestoneAmount + trackerAmount + emergencyAmount;
			} else {
				// No milestones, use contract amount
				totalAmount = Number(contractForm.amount) || 0;
			}
		}

		if (totalAmount < 10) {
			setCurrentTotalAmount(totalAmount);
			setShowAmountWarningDialog(true);
			return;
		}

		if (Number(contractForm.type?.id) === ContractTypeEnum.Fixed) {
			const amountNumber = Number(contractForm.amount) || 0;
			const total = (milestones || []).reduce((sum, m) => sum + (Number(m.amount) || 0), 0);
			
			if (amountNumber <= 0) {
				setMilestonesTotalError(t("contractMilestoneModal.contractAmountError"));
				return;
			}
			if (total !== amountNumber) {
				setMilestonesTotalError(t("contractMilestoneModal.milestonesTotalError", { total, amount: amountNumber }));
				return;
			}

			// Validate first milestone against deposit rules
			if (milestones && milestones.length > 0) {
				const firstMilestone = milestones[0];
				const firstMilestoneAmount = Number(firstMilestone.amount) || 0;
				
				// Calculate total contract amount (including additional costs)
				const totalContractAmount = amountNumber + (contractForm.enableTracker ? getWebWorkTrackerFee(setting) : 0) + (isEmergency ? emergencyCost : 0);

				// Check deposit rules
				const applicableRule = findApplicableDepositRule(
					totalContractAmount,
					depositRules,
					user?.id
				);

				if (applicableRule) {
					// Calculate required deposit amount
					const requiredDepositAmount = calculateDepositAmount(totalContractAmount, applicableRule.rule);

					// Check if first milestone meets minimum deposit requirement
					if (firstMilestoneAmount < requiredDepositAmount) {
						const ruleTypeId = parseInt(applicableRule.rule.type.id);
						const depositDescription = ruleTypeId === DepositRuleTypeEnum.Percentage
							? `${applicableRule.rule.amount}% (${getDynamicCurrencySymbol()}${requiredDepositAmount.toFixed(2)})`
							: `${getDynamicCurrencySymbol()}${requiredDepositAmount.toFixed(2)}`;

						setMilestonesTotalError(
							`⚠️ DEPOSIT REQUIREMENT: First milestone amount must be at least ${depositDescription} based on the deposit rule: "${applicableRule.rule.threshold}". Current first milestone: ${getDynamicCurrencySymbol()}${firstMilestoneAmount.toFixed(2)}`
						);
						return;
					}
				}
			}
		}

		// Only validate "today or later" when creating new contracts, not when editing
		const isEditing = !!contractId;
		const todayStart = new Date();
		todayStart.setHours(0, 0, 0, 0);
		const rowErrors: Record<number, string> = {};
		(milestones || []).forEach((m, idx) => {
			// First milestone (index 0) - due date is optional
			if (idx === 0) {
				if (m.dueDate && !isEditing) {
					const d = new Date(m.dueDate);
					d.setHours(0, 0, 0, 0);
					if (d < todayStart) {
						rowErrors[idx] = t("contractMilestoneModal.dueDateTodayOrLater");
					}
				}
			} else {
				// Second and subsequent milestones - due date is mandatory
				if (!m.dueDate) {
					rowErrors[idx] = t("contractMilestoneModal.dueDateRequiredError");
				} else {
					const d = new Date(m.dueDate);
					d.setHours(0, 0, 0, 0);

					// Only validate "today or later" when creating new contracts
					if (!isEditing && d < todayStart) {
						rowErrors[idx] = t("contractMilestoneModal.dueDateTodayOrLater");
					}

					// Always validate milestone date ordering
					if (idx > 0) {
						const previousMilestone = milestones[idx - 1];
						if (previousMilestone && previousMilestone.dueDate) {
							const prevDate = new Date(previousMilestone.dueDate);
							prevDate.setHours(0, 0, 0, 0);
							if (d < prevDate) {
								rowErrors[idx] = t("contractMilestoneModal.dueDateAfterPrevious");
							}
						}
					}
				}
			}
		});
		if (Object.keys(rowErrors).length > 0) {
			setMilestoneRowErrors(rowErrors);
			return;
		}
		try {
			if (contractId || contractCreated) {

				// Prepare contract data with removed files information
				const contractData = {
					...contractForm,
					removedFiles: removedFiles.map(file => ({ id: file.id }))
				};

				// Update contract
				const res: any = await dispatch(updateContract({ id: contractId, data: contractData }) as any);
				if (res?.meta?.requestStatus === "fulfilled") {
					// Handle milestone operations for existing contract
					if (contractForm.type?.id === ContractTypeEnum.Fixed && contractForm.isMilestone) {
						await handleMilestoneOperations(contractId);
						if (hasFundedEscrow()) {
							// Has funded escrow, close the modal
							GlobalService.showToast("success", t("common.success"), t("contractMilestoneModal.contractUpdatedSuccess"));
							handleClose();
						} else {
							// No funded escrow, open milestone dialog
							setShowMilestonesDialog(true);
						}
					} else if (contractForm.type?.id === ContractTypeEnum.Hourly) {
						await updateHourlyMilestone(contractId);
						if (hasFundedEscrow()) {
							// Has funded escrow, close the modal
							GlobalService.showToast("success", t("common.success"), t("contractMilestoneModal.contractUpdatedSuccess"));
							handleClose();
						} else {
							// No funded escrow, open milestone dialog
							setShowMilestonesDialog(true);
						}
					} else {
						// Fixed contract without milestones
						GlobalService.showToast("success", t("common.success"), t("contractMilestoneModal.contractUpdatedSuccess"));
						handleClose();
					}
				} else {
					throw new Error(res?.error?.message || "Failed to update contract");
				}
			} else {
				const clientId = (currentOffer as any)?.job?.userId?.id as string | undefined;
				const payloadToSend = {
					...contractForm,
					professional: currentOffer?.user?.id ? { id: String(currentOffer.user.id) } : undefined,
					client: clientId ? { id: String(clientId) } : undefined,
					isMilestone: contractForm.type?.id === ContractTypeEnum.Fixed && Array.isArray(milestones) && milestones.length > 0,
				};
				const created: any = await dispatch(createContract(payloadToSend as any) as any);

				const newContractId = created?.payload?.id || created?.id;
				if (!newContractId) throw new Error("Failed to create contract");
				setContractForm((prev: typeof contractForm) => ({ ...prev, contractId: newContractId }));
				setContractId(newContractId);
				setContractCreated(true);

				// Handle different contract types
				if (contractForm.type?.id === ContractTypeEnum.Fixed && Array.isArray(milestones) && milestones.length > 0) {
					// Fixed contract with milestones - only create if not already created
					if (!milestonesCreated) {
						for (let i = 0; i < milestones.length; i++) {
							const m = milestones[i];
							if (!m.description || !m.amount) continue;

							const dto: MilestoneCreateDto = {
								status: { id: MilestoneStatusEnum.Pending },
								description: m.description,
								amount: Number(m.amount) || 0,
								emergencyCost: i === 0 && isEmergency ? emergencyCost : 0,
								trackerAmount: i === 0 && contractForm.enableTracker ? getWebWorkTrackerFee(setting) : 0,
								dueDate: m.dueDate ? new Date(m.dueDate).toISOString() : new Date().toISOString(),
								contract: { id: newContractId },
							} as any;
							await dispatch(createMilestone(dto) as any);
						}
						// Refresh contract to get updated milestone data
						await dispatch(fetchContract(newContractId));
						setMilestonesCreated(true);
					}
					setShowMilestonesDialog(true);
				} else if (contractForm.type?.id === ContractTypeEnum.Hourly) {
					// Hourly contract - create milestone and open milestone dialog
					if (!milestonesCreated) {
						await createHourlyMilestone(newContractId);
						// Refresh contract to get updated milestone data
						await dispatch(fetchContract(newContractId));
						setMilestonesCreated(true);
					}
					setShowMilestonesDialog(true);
				} else {
					// Fixed contract without milestones
					GlobalService.showToast("success", t("common.success"), t("contractMilestoneModal.contractCreatedSuccess"));
					handleClose();
				}
			}

		} catch (err: any) {
			GlobalService.showToast("error", t("common.error"), err?.message || t("contractMilestoneModal.somethingWentWrong"));
		}
	};

	return (
		<AnimatePresence>
			<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4">
				<motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }} className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto relative z-[10000]">
					<div className="sticky top-0 bg-white border-b p-6 flex justify-between items-center relative z-[10001]">
						<div>
							<h2 className="text-2xl font-bold text-gray-900">{contractId ? t("contractMilestoneModal.editContract") : t("contractMilestoneModal.createContract")}</h2>
							<p className="text-gray-600 mt-1">{t("contractMilestoneModal.manageContracts")}</p>
							<span className="text-sm text-red-600">{t("contractMilestoneModal.paymentBasedOnProgress")}</span>
						</div>
						<div className="flex items-center gap-3">
							{contractId && (
								<Button
									type="button"
									variant="outline"
									onClick={() => setIsEditMode(!isEditMode)}
									className="flex items-center gap-2"
								>
									{isEditMode ? t("contractMilestoneModal.viewOnly") : t("contractMilestoneModal.editDetail")}
								</Button>
							)}
							<button onClick={handleClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors"><X className="h-6 w-6 text-gray-500" /></button>
						</div>
					</div>

					<form onSubmit={onSubmit} className="p-6 space-y-6">
						<Card className="p-4 bg-gray-50">
							<div className="flex items-center gap-3">
								<div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
									{(() => {
										const c = currentOffer?.user || currentContract?.professional;
										return c?.photo?.path ? (
											<img
												src={c?.photo?.path}
												alt={c?.firstName + " " + c?.lastName || "Professional"}
												className="h-full w-full object-cover rounded-full"
											/>
										) : (
											<User className="h-6 w-6 text-gray-600" />
										);
									})()}
								</div>
								<div>
									<h3 className="font-semibold text-gray-900">{currentOffer?.id ? currentOffer?.user?.["firstName"] + " " + currentOffer?.user?.["lastName"] : currentContract?.professional?.firstName + " " + currentContract?.professional?.lastName}</h3>
									<p className="text-sm text-gray-600">{t("contractMilestoneModal.fillRequiredFields")}</p>
								</div>
							</div>
						</Card>

						<div className="space-y-4">
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-2">{t("contractMilestoneModal.offer")}</label>
								{clientOffers && clientOffers.length > 0 ? (
									<select
										className="w-full px-3 py-2 border rounded-lg"
										value={selectedOfferFromDropdown?.id || ""}
										onChange={(e) => {
											const selectedId = e.target.value;
											const selected = clientOffers.find(offer => offer.id === selectedId);
											setSelectedOfferFromDropdown(selected);
											if (onOfferSelect) {
												onOfferSelect(selected);
											}
										}}
									>
										<option value="">{t("contractMilestoneModal.selectOffer")}</option>
										{clientOffers.map((offer) => (
											<option key={offer.id} value={offer.id}>
												{offer.title || offer.description || `Offer #${offer.id}`}
											</option>
										))}
									</select>
								) : (
									<Card className="p-3">
										<div className="text-sm font-medium text-gray-900">{currentOffer?.id ? currentOffer?.title || currentOffer?.description || t("contractMilestoneModal.selectedOffer") : currentContract?.title || currentContract?.description || t("contractMilestoneModal.selectedOffer")}</div>
										{!currentOffer?.id && !currentContract?.id && (
											<p className="text-xs text-amber-600 mt-1">{t("contractMilestoneModal.noOfferWarning")}</p>
										)}
									</Card>
								)}
							</div>
							<div className="grid grid-cols gap-4">
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-2">{t("contractMilestoneModal.title")}</label>
									<input
										className={`w-full px-3 py-2 border rounded-lg ${(!isEditMode && contractId) || hasFundedEscrowPayment ? "bg-gray-100 cursor-not-allowed" : ""}`}
										value={contractForm.title}
										onChange={(e) => setContractForm({ ...contractForm, title: e.target.value })}
										readOnly={(!isEditMode && !!contractId) || hasFundedEscrowPayment}
									/>
								</div>
							</div>
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-2">{t("contractMilestoneModal.description")}</label>
								<textarea
									rows={3}
									className={`w-full px-3 py-2 border rounded-lg ${(!isEditMode && contractId) || hasFundedEscrowPayment ? "bg-gray-100 cursor-not-allowed" : ""}`}
									value={contractForm.description}
									onChange={(e) => setContractForm({ ...contractForm, description: e.target.value })}
									readOnly={(!isEditMode && !!contractId) || hasFundedEscrowPayment}
								/>
							</div>
							<div className="grid grid-cols gap-4">
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-2">{t("contractMilestoneModal.type")}</label>
									<div className="grid grid-cols-2 gap-4">
										<button
											onClick={(e) => {
												e.preventDefault();
												if (isEditMode || !contractId) {
													setContractForm(prev => ({
														...prev,
														type: { id: ContractTypeEnum.Fixed },
														enableTracker: false,
														isRenewable: false
													}));
												}
											}}
											disabled={!isEditMode && !!contractId}
											className={`p-4 rounded-lg border-2 transition-colors ${Number(contractForm.type?.id) === ContractTypeEnum.Fixed ? "border-blue-500 bg-blue-50" : "border-gray-200 hover:border-blue-200"} ${!isEditMode && contractId ? "opacity-50 cursor-not-allowed" : ""}`}>
											<h3 className="font-medium">{t("contractMilestoneModal.fixedPrice")}</h3>
											<p className="text-sm text-gray-500">{t("contractMilestoneModal.payFixedAmount")}</p>
										</button>
										<button
											onClick={(e) => { e.preventDefault(); if (isEditMode || !contractId) setContractForm(prev => ({ ...prev, type: { id: ContractTypeEnum.Hourly } })); }}
											disabled={!isEditMode && !!contractId}
											className={`p-4 rounded-lg border-2 transition-colors ${Number(contractForm.type?.id) === ContractTypeEnum.Hourly ? "border-blue-500 bg-blue-50" : "border-gray-200 hover:border-blue-200"} ${!isEditMode && contractId ? "opacity-50 cursor-not-allowed" : ""}`}>
											<h3 className="font-medium">{t("contractMilestoneModal.hourlyRate")}</h3>
											<p className="text-sm text-gray-500">{t("contractMilestoneModal.payByHour")}</p>
										</button>
									</div>
								</div>
							</div>
							<div className="grid grid-cols gap-4">
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
										{contractForm.type?.id === ContractTypeEnum.Fixed ? t("contractMilestoneModal.amount") : t("contractMilestoneModal.hourlyRate")} *
										{hasAnyFundedMilestone() && (
											<span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
												{t("contractMilestoneModal.funded")}
											</span>
										)}
									</label>
									<input
										type="number"
										min={0}
										className={`w-full px-3 py-2 border rounded-lg ${(!isEditMode && contractId) || hasAnyFundedMilestone() ? "bg-gray-100 cursor-not-allowed" : ""}`}
										value={contractForm.amount}
										onChange={(e) => setContractForm({ ...contractForm, amount: Number(e.target.value) || null })}
										readOnly={(!isEditMode && !!contractId) || hasAnyFundedMilestone()}
										disabled={hasAnyFundedMilestone()}
									/>
									{hasAnyFundedMilestone() && (
										<p className="text-xs text-amber-600 mt-1">{t("contractMilestoneModal.amountCannotChange")}</p>
									)}
								</div>
							</div>

							{/* Emergency Cost Display */}
							{isEmergency && emergencyCost > 0 && (
								<div className="bg-red-50 border border-red-200 rounded-lg p-4">
									<div className="flex items-center gap-2 mb-2">
										<div className="w-2 h-2 bg-red-500 rounded-full"></div>
										<h3 className="font-semibold text-red-800">{t("contractMilestoneModal.emergencyService")}</h3>
									</div>
                                                <div className="space-y-2">
                                                    <div className="flex justify-between text-sm">
                                                        <span className="text-red-700">{t("contractMilestoneModal.emergencyCost")}</span>
                                                        <span className="font-medium text-red-800">{getDynamicCurrencySymbol()}{emergencyCost.toFixed(2)}</span>
                                                    </div>
										{emergencyTimeSlot && (
											<div className="flex justify-between text-sm">
												<span className="text-red-700">{t("contractMilestoneModal.timeSlot")}</span>
												<span className="text-red-800">
													{Number(emergencyTimeSlot.id) === 1 ? t("contractMilestoneModal.day") :
														Number(emergencyTimeSlot.id) === 2 ? t("contractMilestoneModal.evening") :
															Number(emergencyTimeSlot.id) === 3 ? t("contractMilestoneModal.night") : t("contractMilestoneModal.unknown")}
												</span>
											</div>
										)}
										{emergencyDays && (
											<div className="flex justify-between text-sm">
												<span className="text-red-700">{t("contractMilestoneModal.dayType")}</span>
												<span className="text-red-800">
													{Number(emergencyDays.id) === 1 ? t("contractMilestoneModal.weekday") :
														Number(emergencyDays.id) === 2 ? t("contractMilestoneModal.weekend") :
															Number(emergencyDays.id) === 3 ? t("contractMilestoneModal.holiday") : t("contractMilestoneModal.unknown")}
												</span>
											</div>
										)}
									</div>
								</div>
							)}
							{contractForm.type?.id === ContractTypeEnum.Hourly && (
								<div className="grid grid-cols gap-4">
									<div>
										<label className="block text-sm font-medium text-gray-700 mb-2">{t("contractMilestoneModal.hourlyType")}</label>
										<div className="flex items-center gap-6">
											<label className={`flex items-center gap-2 ${!isEditMode && contractId ? "opacity-50 cursor-not-allowed" : ""}`}>
												<input
													type="radio"
													name="hourlyType"
													value={ContractHourlyTypeEnum.Occasional}
													checked={Number(contractForm.hourlyType?.id) === ContractHourlyTypeEnum.Occasional}
													onChange={() => setContractForm({ ...contractForm, hourlyType: { id: ContractHourlyTypeEnum.Occasional } })}
													disabled={!isEditMode && !!contractId}
												/>
												<span>{t("contractMilestoneModal.occasional")}</span>
											</label>
											<label className={`flex items-center gap-2 ${!isEditMode && contractId ? "opacity-50 cursor-not-allowed" : ""}`}>
												<input
													type="radio"
													name="hourlyType"
													value={ContractHourlyTypeEnum.Long_Term}
													checked={Number(contractForm.hourlyType?.id) === ContractHourlyTypeEnum.Long_Term}
													onChange={() => setContractForm({ ...contractForm, hourlyType: { id: ContractHourlyTypeEnum.Long_Term } })}
													disabled={!isEditMode && !!contractId}
												/>
												<span>{t("contractMilestoneModal.longTerm")}</span>
											</label>
										</div>
									</div>
								</div>
							)}

							{contractForm.type?.id === ContractTypeEnum.Hourly && (
								<div className="grid grid-cols-2 gap-4">
									<div>
										<label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
											{t("contractMilestoneModal.weeklyLimit")}
											{hasAnyFundedMilestone() && (
												<span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
													{t("contractMilestoneModal.funded")}
												</span>
											)}
										</label>
										<input
											type="number"
											min={0}
											className={`w-full px-3 py-2 border rounded-lg ${(!isEditMode && contractId) || hasAnyFundedMilestone() ? "bg-gray-100 cursor-not-allowed" : ""}`}
											value={contractForm.weeklyLimit}
											onChange={(e) => setContractForm({ ...contractForm, weeklyLimit: Number(e.target.value) || 0 })}
											readOnly={(!isEditMode && !!contractId) || hasAnyFundedMilestone()}
											disabled={hasAnyFundedMilestone()}
										/>
										{hasAnyFundedMilestone() && (
											<p className="text-xs text-amber-600 mt-1">{t("contractMilestoneModal.weeklyLimitCannotChange")}</p>
										)}
									</div>
									<div className={`flex items-center gap-2 ${!isEditMode && contractId ? "opacity-50 cursor-not-allowed" : ""}`}>
										<input
											type="checkbox"
											checked={contractForm.canAutomaticWithdraw}
											onChange={(e) => setContractForm({ ...contractForm, canAutomaticWithdraw: e.target.checked })}
											disabled={!isEditMode && !!contractId}
										/>
										<label className="text-sm">{t("contractMilestoneModal.authorizeWithdrawal")}</label>
									</div>
								</div>
							)}
							{contractForm.type?.id === ContractTypeEnum.Hourly && (
								<div className="space-y-4">
									<div className={`flex items-center gap-2 ${!isEditMode && contractId ? "opacity-50 cursor-not-allowed" : ""}`}>
										<input
											type="checkbox"
											checked={contractForm.enableTracker}
											onChange={(e) => setContractForm({ ...contractForm, enableTracker: e.target.checked })}
											disabled={(!isEditMode && !!contractId) || hasFundedEscrowPayment}
										/>
										<label className="text-sm">{t("contractMilestoneModal.smartWorkTracker")}
											<br />
											<span className="text-xs text-gray-500">{t("contractMilestoneModal.recommendedForLongTerm")}</span>
										</label>

									</div>

									{contractForm.enableTracker && (
										<div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
											<div className="text-center mb-6">
												<h3 className="text-lg font-bold text-gray-900 mb-2">
													{t("contractMilestoneModal.trackerTitle")}
												</h3>
												<p className="text-sm text-gray-600 mb-1">
													{t("contractMilestoneModal.trackerSubtitle1")}
												</p>
												<p className="text-sm text-gray-600">
													{t("contractMilestoneModal.trackerSubtitle2")}
												</p>
											</div>

											<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
												{/* Benefits for Customer */}
												<div>
													<h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
														<span className="w-2 h-2 bg-blue-600 rounded-full"></span>
														{t("contractMilestoneModal.benefitsForCustomer")}
													</h4>
													<ul className="space-y-2">
														<li className="flex items-start gap-2 text-sm text-gray-700">
															<span className="text-green-500 mt-0.5">✓</span>
															<span>{t("contractMilestoneModal.customerBenefit1")}</span>
														</li>
														<li className="flex items-start gap-2 text-sm text-gray-700">
															<span className="text-green-500 mt-0.5">✓</span>
															<span>{t("contractMilestoneModal.customerBenefit2")}</span>
														</li>
														<li className="flex items-start gap-2 text-sm text-gray-700">
															<span className="text-green-500 mt-0.5">✓</span>
															<span>{t("contractMilestoneModal.customerBenefit3")}</span>
														</li>
													</ul>
												</div>

												{/* Benefits for Freelancers */}
												<div>
													<h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
														<span className="w-2 h-2 bg-blue-600 rounded-full"></span>
														{t("contractMilestoneModal.benefitsForFreelancers")}
													</h4>
													<ul className="space-y-2">
														<li className="flex items-start gap-2 text-sm text-gray-700">
															<span className="text-green-500 mt-0.5">✓</span>
															<span>{t("contractMilestoneModal.freelancerBenefit1")}</span>
														</li>
														<li className="flex items-start gap-2 text-sm text-gray-700">
															<span className="text-green-500 mt-0.5">✓</span>
															<span>{t("contractMilestoneModal.freelancerBenefit2")}</span>
														</li>
														<li className="flex items-start gap-2 text-sm text-gray-700">
															<span className="text-green-500 mt-0.5">✓</span>
															<span>{t("contractMilestoneModal.freelancerBenefit3")}</span>
														</li>
													</ul>
												</div>
											</div>
										</div>
									)}
								</div>
							)}

							{/* Milestone Option */}
							{contractForm.type?.id === ContractTypeEnum.Fixed && (
								<div className="space-y-3">
									{/* <div className={`flex items-center gap-2 ${!isEditMode && contractId ? "opacity-50 cursor-not-allowed" : ""}`}>
										<input
											type="checkbox"
											checked={contractForm.isMilestone}
											onChange={(e) => setContractForm({ ...contractForm, isMilestone: e.target.checked })}
											disabled={!isEditMode && !!contractId}
										/>
										<label className="text-sm text-gray-700">
											Need Milestone
										</label>
										<span className="text-sm text-red-600">(Payment based on work progress)</span>
									</div> */}
									{contractId && contractForm.isMilestone && (
										<div className="flex items-center gap-2">
											<Button
												type="button"
												variant="outline"
												onClick={() => setShowMilestonesDialog(true)}
												className="flex items-center gap-2"
											>
												<Clock className="w-4 h-4" />
												{t("contractMilestoneModal.viewMilestones")}
											</Button>
										</div>
									)}
								</div>
							)}


							{/* Milestones for Fixed contracts */}
							{contractForm.type?.id === ContractTypeEnum.Fixed && contractForm.isMilestone && (
								<div className="space-y-4">
									{milestones.map((m, idx) => (
										<div key={`milestone-${m.id || "new"}-${idx}`} className="border rounded-lg p-4 relative">
											{(isEditMode || !contractId) && !(m.id && isMilestoneFunded(m.id)) ? (
												<div className="absolute right-2 top-2">
													<button onClick={(e) => { e.preventDefault(); removeMilestoneRow(idx); }} className="p-2 rounded hover:bg-gray-100" aria-label="Remove Milestone">
														<Trash2 className="w-4 h-4 text-gray-500" />
													</button>
												</div>
											) : null}
											<div className="text-sm font-semibold text-gray-800 mb-2 flex items-center gap-2">
												{t("contractMilestoneModal.milestone")} {idx + 1}
												{m.id && <span className="text-xs text-gray-500">{t("contractMilestoneModal.milestoneId", { id: m.id })}</span>}
												{m.id && isMilestoneFunded(m.id) && (
													<span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
														{t("contractMilestoneModal.funded")}
													</span>
												)}
											</div>
											<div className="mb-3">
												<label className="block text-sm font-medium text-gray-700 mb-2">{t("contractMilestoneModal.descriptionLabel")}</label>
												<input
													className={`w-full px-3 py-2 border rounded-lg ${!isEditMode && contractId ? "bg-gray-100 cursor-not-allowed" : ""}`}
													placeholder={t("contractMilestoneModal.whatIsTask")}
													value={m.description}
													onChange={(e) => updateMilestoneRow(idx, { description: e.target.value })}
													readOnly={!isEditMode && !!contractId}
												/>
											</div>
											<div className="grid grid-cols-2 gap-4">
												<div>
													<label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
														{t("contractMilestoneModal.amountLabel")}
														{m.id && isMilestoneFunded(m.id) && (
															<span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
																{t("contractMilestoneModal.funded")}
															</span>
														)}
													</label>
													<input
														type="number"
														min={0}
														className={`w-full px-3 py-2 border rounded-lg ${(!isEditMode && contractId) || (m.id && isMilestoneFunded(m.id)) ? "bg-gray-100 cursor-not-allowed" : ""}`}
														value={m.amount}
														onChange={(e) => updateMilestoneRow(idx, { amount: Number(e.target.value) || null })}
														readOnly={(!isEditMode && !!contractId) || (m.id && isMilestoneFunded(m.id))}
														disabled={m.id && isMilestoneFunded(m.id)}
													/>
													{m.id && isMilestoneFunded(m.id) && (
														<p className="text-xs text-amber-600 mt-1">{t("contractMilestoneModal.amountFundedCannotChange")}</p>
													)}
												</div>
												<div>
													<label className="block text-sm font-medium text-gray-700 mb-2">
														{t("contractMilestoneModal.dueDate")} {idx === 0 ? t("contractMilestoneModal.dueDateOptional") : t("contractMilestoneModal.dueDateRequired")}
													</label>
													<input
														type="date"
														className={`w-full px-3 py-2 border rounded-lg ${milestoneRowErrors[idx] ? "border-red-500" : ""} ${!isEditMode && contractId ? "bg-gray-100 cursor-not-allowed" : ""}`}
														value={m.dueDate ? m.dueDate.substring(0, 10) : ""}
														onChange={(e) => updateMilestoneRow(idx, { dueDate: e.target.value ? new Date(e.target.value).toISOString() : undefined })}
														onClick={(e) => {
															if (isEditMode || !contractId) {
																// Try to show the calendar picker if supported
																try {
																	(e.target as HTMLInputElement).showPicker?.();
																} catch (error) {
																	// Fallback: focus the input if showPicker is not supported
																	(e.target as HTMLInputElement).focus();
																}
															}
														}}
														readOnly={!isEditMode && !!contractId}
													/>
													{milestoneRowErrors[idx] && (<p className="text-red-500 text-xs mt-1">{milestoneRowErrors[idx]}</p>)}
												</div>
											</div>
										</div>
									))}
									{milestonesTotalError && (
										<p className="text-red-600 text-sm">{milestonesTotalError}</p>
									)}
                                        {Number(contractForm.amount) > 0 && (
                                            <div className="space-y-1">
                                                <p className="text-gray-600 text-sm">Milestones total: {(milestones || []).reduce((s, m) => s + (Number(m.amount) || 0), 0)} / Contract amount: {Number(contractForm.amount) || 0}</p>
                                                {isEmergency && emergencyCost > 0 && (
                                                    <p className="text-red-600 text-sm">Emergency cost: {getDynamicCurrencySymbol()}{emergencyCost.toFixed(2)} (stored separately in first milestone)</p>
                                                )}
                                            </div>
                                        )}
									{(isEditMode || !contractId) && (
										<div>
											<Button type="button" variant="outline" onClick={addMilestoneRow}>{t("contractMilestoneModal.addMilestone")}</Button>
										</div>
									)}
								</div>
							)}

							{/* Hourly Payment Summary */}
							{contractForm.type?.id === ContractTypeEnum.Hourly && contractForm.amount && contractForm.weeklyLimit && (
                                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                                <h3 className="font-semibold text-gray-900 mb-3">{t("contractMilestoneModal.paymentSummary")}</h3>
                                                <div className="space-y-2">
                                                    <div className="flex justify-between text-sm">
                                                        <span className="text-gray-600">{t("contractMilestoneModal.hourlyRateLabel")}</span>
                                                        <span className="font-medium">{getDynamicCurrencySymbol()}{contractForm.amount}</span>
                                                    </div>
                                                    <div className="flex justify-between text-sm">
                                                        <span className="text-gray-600">{t("contractMilestoneModal.weeklyHours")}</span>
                                                        <span className="font-medium">{contractForm.weeklyLimit} {t("contractMilestoneModal.hours")}</span>
                                                    </div>
                                                    <div className="flex justify-between text-sm">
                                                        <span className="text-gray-600">{t("contractMilestoneModal.weeklyAmount")}</span>
                                                        <span className="font-medium">{getDynamicCurrencySymbol()}{(contractForm.amount * contractForm.weeklyLimit).toFixed(2)}</span>
                                                    </div>

                                                    <div className="flex justify-between text-sm font-medium border-t pt-2">
                                                        <span>{t("contractMilestoneModal.subtotal")}</span>
                                                        <span>{getDynamicCurrencySymbol()}{(contractForm.amount * contractForm.weeklyLimit).toFixed(2)}</span>
                                                    </div>

                                                    {/* Smart Work Tracker - shown separately */}
                                                    {contractForm.enableTracker && (
                                                        <div className="flex justify-between text-sm">
                                                            <span className="text-blue-600">{t("contractMilestoneModal.smartWorkTrackerLabel")}</span>
                                                            <span className="font-medium text-blue-600">{getDynamicCurrencySymbol()}{getWebWorkTrackerFee(setting).toFixed(2)}</span>
                                                        </div>
                                                    )}

                                                    {/* Emergency Cost - shown separately */}
                                                    {isEmergency && emergencyCost > 0 && (
                                                        <div className="flex justify-between text-sm">
                                                            <span className="text-red-600">{t("contractMilestoneModal.emergencyCostLabel")}</span>
                                                            <span className="font-medium text-red-600">{getDynamicCurrencySymbol()}{emergencyCost.toFixed(2)}</span>
                                                        </div>
                                                    )}

										<div className="flex justify-between text-sm">
											<span className="text-gray-600">{PLATFORM_CONFIG.PLATFORM_FEE_LABEL} ({PlatformFeeUtils.getPlatformFeePercentageDisplay()}):</span>
											<span className="font-medium">{PlatformFeeUtils.formatPlatformFee(calculateHourlyMilestoneAmount() + (contractForm.enableTracker ? getWebWorkTrackerFee(setting) : 0) + (isEmergency ? emergencyCost : 0))}</span>
										</div>
										<div className="flex justify-between text-sm font-semibold border-t pt-2 mt-2">
											<span>{t("contractMilestoneModal.totalPayment")}</span>
											<span className="text-blue-600">{PlatformFeeUtils.formatTotalWithFee(calculateHourlyMilestoneAmount() + (contractForm.enableTracker ? getWebWorkTrackerFee(setting) : 0) + (isEmergency ? emergencyCost : 0))}</span>
										</div>
									</div>
								</div>
							)}

							{/* Contract Renewal Option */}
							{contractForm.type?.id === ContractTypeEnum.Hourly && (
								<div className="flex flex-row justify-between space-y-3">
									<div className={`flex items-center gap-2 ${!isEditMode && contractId ? "opacity-50 cursor-not-allowed" : ""}`}>
										<input
											type="checkbox"
											checked={contractForm.isRenewable}
											onChange={(e) => setContractForm({ ...contractForm, isRenewable: e.target.checked })}
											disabled={!isEditMode && !!contractId}
										/>
										<label className="text-sm text-black-700">
											{t("contractMilestoneModal.enableRenewal")}
											<br />
											<span className="text-xs text-gray-500">{t("contractMilestoneModal.renewalDescription")}</span>
										</label>
									</div>
									<div className="flex items-center gap-2">
										<button
											type="button"
											onClick={() => setShowFeaturesDialog(true)}
											className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 underline"
										>
											<Info className="w-4 h-4" />
											{t("contractMilestoneModal.seeServices")}
										</button>
									</div>
								</div>
							)}

							<div>
								<label className="block text-sm font-medium text-gray-700 mb-2">{t("contractMilestoneModal.files")}</label>
								<div className={`border-2 border-dashed border-gray-300 rounded-lg p-6 text-center ${!isEditMode && contractId ? "opacity-50 cursor-not-allowed" : ""}`}>
									<input
										type="file"
										multiple
										onChange={handleFileUpload}
										className="hidden"
										id="contract-file-upload"
										accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
										disabled={isUploading || (!isEditMode && !!contractId)}
									/>
									<label htmlFor="contract-file-upload" className={`cursor-pointer ${isUploading || (!isEditMode && !!contractId) ? "opacity-50" : ""}`}>
										{isUploading ? (
											<>
												<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
												<p className="mt-2 text-sm text-gray-600">{t("contractMilestoneModal.uploading")}</p>
												{uploadingFileName && (<p className="text-xs text-gray-500 mt-1">{uploadingFileName}</p>)}
											</>
										) : (
											<>
												<Upload className="mx-auto h-12 w-12 text-gray-400" />
												<p className="mt-2 text-sm text-gray-600">{t("contractMilestoneModal.clickToUpload")}</p>
												<p className="text-xs text-gray-500 mt-1">{t("contractMilestoneModal.fileTypes")}</p>
											</>
										)}
									</label>
								</div>
								{uploadedFiles.length > 0 && (
									<div className="mt-4 space-y-2">
									{uploadedFiles.map((file, idx) => (
										<div key={`uploaded-file-${file.id || file.name || idx}`} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
												<div className="flex items-center gap-2">
													<FileText className="w-4 h-4 text-gray-500" />
													<span className="text-sm text-gray-700">{file.name}</span>
												</div>
												{(isEditMode || !contractId) && (
													<button
														type="button"
														onClick={() => handleFileRemove(file)}
														className="p-1 hover:bg-red-100 rounded text-red-500 hover:text-red-700 transition-colors"
														title={t("contractMilestoneModal.removeFile")}
													>
														<X className="w-4 h-4" />
													</button>
												)}
											</div>
										))}
									</div>
								)}
								{/* Debug info */}
								{process.env.NODE_ENV === "development" && (
									<div className="mt-2 text-xs text-gray-500">
										Debug: {uploadedFiles.length} files uploaded
									</div>
								)}
							</div>
						</div>
						{/* Information Buttons */}
						<div className="flex gap-3 pt-4">
							<Button
								type="button"
								variant="outline"
								onClick={() => setShowInformationDialog(true)}
								className="flex-1 flex items-center justify-center gap-2"
							>
								<Info className="w-4 h-4" />
								{t("contractMilestoneModal.information")}
							</Button>
							<Button
								type="button"
								variant="outline"
								onClick={() => setShowVideosDialog(true)}
								className="flex-1 flex items-center justify-center gap-2"
							>
								<FileText className="w-4 h-4" />
								{t("contractMilestoneModal.informationalVideos")}
							</Button>
						</div>
						<div className="pt-2">
							<label className={`flex items-start gap-3 text-sm ${!isEditMode && contractId ? "opacity-50 cursor-not-allowed" : ""}`}>
								<input
									type="checkbox"
									className="mt-1"
									checked={acceptTerms}
									onChange={(e) => setAcceptTerms(e.target.checked)}
									disabled={!isEditMode && !!contractId}
								/>
								<label htmlFor="acceptTerms" className="text-sm text-gray-700 cursor-pointer">
									{t("contractMilestoneModal.agreeToTerms")}{" "}
									<a
										href="/terms-and-conditions"
										target="_blank"
										rel="noopener noreferrer"
										className="text-blue-600 hover:text-blue-800 underline"
									>
										{t("contractMilestoneModal.termsAndConditions")}
									</a>
									{" "}{t("contractMilestoneModal.and")}{" "}
									<a
										href="/privacy-policy"
										target="_blank"
										rel="noopener noreferrer"
										className="text-blue-600 hover:text-blue-800 underline"
									>
										{t("contractMilestoneModal.privacyPolicy")}
									</a>
									{t("contractMilestoneModal.understandServiceTerms")}
								</label>
							</label>
						</div>



						<div className="flex gap-3 pt-4">
							<Button type="button" variant="outline" onClick={handleClose} className="flex-1" disabled={isSubmitting}>{t("contractMilestoneModal.cancel")}</Button>
							{(!contractId || isEditMode) && (
								<Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700" disabled={isSubmitting || !acceptTerms}>{isSubmitting ? t("contractMilestoneModal.saving") : t("contractMilestoneModal.save")}</Button>
							)}
						</div>
					</form>
				</motion.div>
			</motion.div>

			{/* Features Dialog */}
			<AnimatePresence>
				{showFeaturesDialog && (
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4"
						onClick={(e) => {
							if (e.target === e.currentTarget) setShowFeaturesDialog(false);
						}}
					>
						<motion.div
							initial={{ scale: 0.95, y: 20 }}
							animate={{ scale: 1, y: 0 }}
							exit={{ scale: 0.95, y: 20 }}
							className="bg-white rounded-xl w-full max-w-2xl max-h-[80vh] overflow-y-auto"
							onClick={(e) => e.stopPropagation()}
						>
							<div className="sticky top-0 bg-white border-b p-6 flex justify-between items-center">
								<div>
									<h2 className="text-xl font-bold text-gray-900">{t("contractMilestoneModal.renewableContractFeatures")}</h2>
									<p className="text-gray-600 mt-1">{t("contractMilestoneModal.advancedTimeTracking")}</p>
								</div>
								<button
									onClick={() => setShowFeaturesDialog(false)}
									className="p-2 hover:bg-gray-100 rounded-full transition-colors"
								>
									<X className="h-6 w-6 text-gray-500" />
								</button>
							</div>

							<div className="p-6">
								<div className="space-y-4">
									{renewableFeatures.map((feature, index) => (
										<div key={`renewable-feature-${index}-${feature.substring(0, 20)}`} className="flex items-start gap-3">
											<div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
												<span className="text-blue-600 text-sm font-medium">{index + 1}</span>
											</div>
											<p className="text-sm text-gray-700">{feature}</p>
										</div>
									))}
								</div>

								<div className="mt-8 p-4 bg-blue-50 rounded-lg">
									<h3 className="font-semibold text-gray-900 mb-2">{t("contractMilestoneModal.whatsIncluded")}</h3>
									<p className="text-sm text-gray-600">
										{t("contractMilestoneModal.whatsIncludedDescription")}
									</p>
								</div>

								<div className="flex justify-end mt-6">
									<Button
										type="button"
										onClick={() => setShowFeaturesDialog(false)}
										className="px-6 py-2"
									>
										{t("contractMilestoneModal.gotIt")}
									</Button>
								</div>
							</div>
						</motion.div>
					</motion.div>
				)}
			</AnimatePresence>

			{/* Milestones Dialog */}
			{contractId && (contractForm.isMilestone || contractForm.type?.id === ContractTypeEnum.Hourly) && (
				<MilestoneDialog
					isOpen={showMilestonesDialog}
					onClose={() => {
						setShowMilestonesDialog(false);
						// Refresh contract data (which includes milestones)
						if (contractId) {
							dispatch(fetchContract(contractId));
						}
					}}
					contractId={contractId}
				/>
			)}

			{/* Information Dialog */}
			<InformationDialog
				isOpen={showInformationDialog}
				onClose={() => setShowInformationDialog(false)}
			/>

			{/* Informational Videos Dialog */}
			<InformationalVideosDialog
				isOpen={showVideosDialog}
				onClose={() => setShowVideosDialog(false)}
			/>

			{/* Amount Warning Dialog */}
			<AnimatePresence>
				{showAmountWarningDialog && (
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4"
						onClick={(e) => {
							if (e.target === e.currentTarget) setShowAmountWarningDialog(false);
						}}
					>
						<motion.div
							initial={{ scale: 0.95, y: 20 }}
							animate={{ scale: 1, y: 0 }}
							exit={{ scale: 0.95, y: 20 }}
							className="bg-white rounded-xl w-full max-w-md p-6"
							onClick={(e) => e.stopPropagation()}
						>
							<div className="flex items-center gap-3 mb-4">
								<div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
									<Info className="h-6 w-6 text-amber-600" />
								</div>
								<div>
									<h2 className="text-xl font-bold text-gray-900">{t("contractMilestoneModal.paymentAmountTooLow")}</h2>
									<p className="text-gray-600">{t("contractMilestoneModal.minimumRequirementNotMet")}</p>
								</div>
							</div>

							<div className="mb-6">
								<p className="text-gray-700 mb-3">
									The total payment amount must be at least <strong>{getDynamicCurrencySymbol()}10.00</strong> to process payments.
								</p>
								<p className="text-sm text-gray-600">
									Current total amount: <span className="font-medium">{getDynamicCurrencySymbol()}{currentTotalAmount.toFixed(2)}</span>
								</p>
								<div className="mt-3 p-3 bg-gray-50 rounded-lg">
									<p className="text-xs text-gray-600 mb-1">{t("contractMilestoneModal.amountBreakdown")}</p>
									<div className="text-xs text-gray-600 space-y-1">
										{Number(contractForm.type?.id) === ContractTypeEnum.Hourly ? (
											<>
												<div className="flex justify-between">
													<span>{t("contractMilestoneModal.baseAmount")}</span>
													<span>{getDynamicCurrencySymbol()}{calculateHourlyMilestoneAmount().toFixed(2)}</span>
												</div>
											</>
										) : (
											<>
												<div className="flex justify-between">
													<span>{t("contractMilestoneModal.firstMilestoneAmount")}</span>
													<span>{getDynamicCurrencySymbol()}{milestones && milestones.length > 0 ? (Number(milestones[0].amount) || 0).toFixed(2) : "0.00"}</span>
												</div>
											</>
										)}
										{contractForm.enableTracker && (
											<div className="flex justify-between">
												<span>{t("contractMilestoneModal.smartWorkTrackerBreakdown")}</span>
												<span>{getDynamicCurrencySymbol()}{getWebWorkTrackerFee(setting).toFixed(2)}</span>
											</div>
										)}
										{isEmergency && emergencyCost > 0 && (
											<div className="flex justify-between">
												<span>{t("contractMilestoneModal.emergencyCostBreakdown")}</span>
												<span>{getDynamicCurrencySymbol()}{emergencyCost.toFixed(2)}</span>
											</div>
										)}
										<div className="flex justify-between font-medium border-t pt-1">
											<span>{t("contractMilestoneModal.total")}</span>
											<span>{getDynamicCurrencySymbol()}{currentTotalAmount.toFixed(2)}</span>
										</div>
									</div>
								</div>
							</div>

							<div className="flex justify-end gap-3">
								<Button
									type="button"
									variant="outline"
									onClick={() => setShowAmountWarningDialog(false)}
									className="px-6"
								>
									{t("contractMilestoneModal.ok")}
								</Button>
							</div>
						</motion.div>
					</motion.div>
				)}
			</AnimatePresence>
		</AnimatePresence>
	);
};

export default ContractMilestoneModal;

